import { useState, useEffect, useCallback } from 'react'
import { useUserSession } from 'features/session/context'
import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { validationMessages } from 'constants/validation'

import {
  TextField,
  FormControl,
  Autocomplete,
  MenuItem,
  Chip,
  Select,
  Box,
  Stack,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText
} from '@mui/material'

import { ErrorInfo } from 'components/controls/errorInfo'
import { Section}  from 'components/controls/section'

import { useListGroup } from 'interface/user/group'
import { useListManufacturer } from 'interface/manufacturer'
import { SearchResult, useListSearchsGroup, SearchFormData, SearchType } from 'interface/search'
import { AddManufacturerDialog } from 'features/manufacturer/dialog/add'
import { MarkdownInput } from 'components/controls/markDownInput'
import { useListEvaluationForm } from 'interface/evaluationForm'
import { ManufacturerOption } from "./components/manufacturerOption"

const {
  Required,
  TooLongLength,
  TooShortLength
} = validationMessages

const searchValidation = yup.object().shape({
  title: yup.string()
    .required(Required)
    .min(1, TooShortLength(1))
    .max(100, TooLongLength(100)),
  manufacturerId: yup.number().required(Required),
  groups: yup.array()
    .min(1, Required)
    .required(Required)
    .of(
      yup.number().required(Required)
    ),
  searchType: yup.string().required(Required),
  request: yup.string().ensure(),
  evaluationFormId: yup.number().required().nullable()
})

export const useSearchForm = (
  search?: SearchResult
) => {
  const { groupId } = useUserSession()

  const manufacturerListState = useListManufacturer()
  const groupListState = useListGroup()
  const searchsGroupState = useListSearchsGroup(search?.searchId)
  const evaluationFormListState = useListEvaluationForm()

  const form = useForm<SearchFormData>({
    defaultValues: {
      title: search?.title ?? '',
      manufacturerId: search?.manufacturer?.manufacturerId ?? 1,
      groups: (!search && groupId) ? [groupId] : [],
      searchType: (search?.searchType ?? 'list') as string,
      request: search?.request ?? '',
      evaluationFormId: search?.evaluationFormId ?? 0
    },
    mode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(searchValidation)
  })
  return {
    search,
    form,
    manufacturerListState,
    groupListState,
    searchsGroupState,
    evaluationFormListState,
    isLoading: (groupListState.loading || searchsGroupState.loading || manufacturerListState.loading)
  }
}

export const SearchForm = ({
  search,
  form: {
    control,
    setValue,
    formState: { errors }
  },
  manufacturerListState,
  groupListState,
  searchsGroupState,
  evaluationFormListState
}: ReturnType<typeof useSearchForm>) => {
  const [modal, setModal] = useState<'manufacturer' | null>(null)
  useEffect(
    () => {
      if (search) {
        setValue("groups", searchsGroupState.data?.list.map((group) => group.groupId) ?? [], { shouldValidate: true })
      }
    },
    [search, searchsGroupState, setValue]
  )
  const onClickAddManufacturer = useCallback(
    () => {
      setModal('manufacturer')
    },
    [setModal]
  )
  return (
    <>
      <Section
        label="タイトル"
        labelWidth="6em"
        labelProps={{ paddingY: 2 }}
      >
        <Controller
          control={control}
          name="title"
          render={({ field }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              error={!!errors.title}
              helperText={errors.title ? errors.title.message : ''}
            />
          )}
        />
      </Section>

      <Section
        label="グループ割当"
        labelWidth="6em"
        labelProps={{ paddingY: 2.5 }}
      >
        {groupListState.error && <ErrorInfo error={groupListState.error} />}
        {searchsGroupState.error && <ErrorInfo error={searchsGroupState.error} />}

        {groupListState.data ? (
          <Controller
            control={control}
            name={'groups'}
            render={({ field }) => (
              <FormControl variant="outlined">
                <Select
                  {...field}
                  labelId="グループ割当"
                  multiple
                  size="small"
                  renderValue={(selected: number[]) => (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                      }}
                    >
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={
                            groupListState.data?.list.find(
                              (group) => group.groupId === value
                            )?.groupName ?? ''
                          }
                          color="success"
                          variant="filled"
                        />
                      ))}
                    </Box>
                  )}
                >
                  {(groupListState.data?.list || []).map((group) => (
                    <MenuItem
                      key={`group-ids-item-${group.groupId}`}
                      value={group.groupId}
                    >
                      {group.groupName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        ) : <></> }
      </Section>

      <Section
        label="製薬会社"
        labelWidth="6em"
        labelProps={{ paddingY: 2 }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <Box flexGrow={1}>
            {manufacturerListState.data ? (
              <Controller
                control={control}
                name="manufacturerId"
                render={({ field }) => (
                  <Autocomplete
                    ref={field.ref}
                    value={manufacturerListState.data?.list.find(
                      (manufacturer) => manufacturer.manufacturerId === field.value
                    ) ?? null}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!errors.manufacturerId}
                        helperText={errors.manufacturerId ? errors.manufacturerId.message : ''}
                      />
                    )}
                    getOptionLabel={(manufacturer) => manufacturer.manufacturerName ?? ''}
                    options={manufacturerListState.data?.list ?? []}
                    renderOption={(props, manufacturer) => (
                      <ManufacturerOption
                        manufacturer={manufacturer}
                        {...props}
                      />
                    )}
                    onChange={(_, value) => {
                      setValue("manufacturerId", value?.manufacturerId ?? 1)
                    }}
                  />
                )}
              />
            ) : (
              <></>
            )}
          </Box>
          <Button
            variant="contained"
            onClick={onClickAddManufacturer}
          >
            追加
          </Button>
        </Stack>
      </Section>

      <Section
        label="依頼内容"
        labelWidth="6em"
        labelProps={{ paddingY: 2 }}
      >
        <Stack direction="column" spacing={3}>
					<Controller
						control={control}
						name="searchType"
						render={({ field }) => (
							<FormControl>
								<RadioGroup
									{...field}
									row
								>
                  <FormControlLabel
                    value={SearchType.List}
                    control={<Radio size="small"/>}
                    label="ターゲット分析"
                  />
                  <FormControlLabel
                    value="extend"
                    control={<Radio size="small"/>}
                    label="ターゲット拡張"
                  />
                  <FormControlLabel
                    value="search"
                    control={<Radio size="small"/>}
                    label="KOLサーチ"
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio size="small"/>}
                    label="その他"
                  />
								</RadioGroup>
							</FormControl>
						)}
					/>
					{errors.searchType && (
						<FormHelperText error>{errors.searchType.message}</FormHelperText>
					)}
        </Stack>

        <Controller
          control={control}
          name="request"
          render={({ field }) => (
            <MarkdownInput
              {...field}
              variant="outlined"
              size="small"
              rows={10}
              error={!!errors.request}
              helperText={errors.request ? errors.request.message : ''}
            />
          )}
        />
      </Section>

      {(evaluationFormListState.data && evaluationFormListState.data.list.length > 0) && (
        <Section
          label="評価フォーム"
          labelWidth="6em"
          labelProps={{ paddingY: 2 }}
        >
          <Controller
            control={control}
            name="evaluationFormId"
            render={({ field }) => (
              <Select
                {...field}
                size="small"
                error={!!errors.evaluationFormId}
              >
                <MenuItem value={0}>（使用しない）</MenuItem>
                {evaluationFormListState.data?.list?.map(
                  form => <MenuItem key={`evaluation-form-${form.formId}`} value={form.formId}>{form.formName}</MenuItem>
                )}
              </Select>
            )}
          />
					{errors.evaluationFormId && (
						<FormHelperText error>{errors.evaluationFormId.message}</FormHelperText>
					)}
        </Section>
      )}

      {modal === 'manufacturer' ? (
        <AddManufacturerDialog
          onAddManufacturer={({ manufacturerId }) => {
            manufacturerListState.refetch()
            setValue("manufacturerId", manufacturerId, { shouldValidate: true })
            setModal(null)
          }}
          onClose={() => setModal(null)}
        />
      ) : <></>}
    </>
  )
}
